/**
 * ViewValidator.ts - Validation logic for view definitions
 */

import type { ViewrView, ViewValidationResult, ViewType } from '../types';

/**
 * Valid view types
 */
const VALID_VIEW_TYPES: ViewType[] = [
  'list', 'cards', 'table', 'spreadsheet',
  'kanban', 'timeline', 'calendar', 'gantt',
  'graph', 'network', 'mindmap', 'tree',
  'dashboard', 'chart', 'metrics',
  'gallery', 'form', 'detail', 'preview', 'outliner', 'notes',
  'map', 'geospatial',
];

/**
 * Validate a view definition
 */
export function validateView(view: ViewrView): ViewValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Required fields
  if (!view.id) {
    errors.push('View ID is required');
  } else if (typeof view.id !== 'string') {
    errors.push('View ID must be a string');
  } else if (!/^[a-z0-9-]+$/.test(view.id)) {
    errors.push('View ID must contain only lowercase letters, numbers, and hyphens');
  }

  if (!view.name) {
    errors.push('View name is required');
  } else if (typeof view.name !== 'string') {
    errors.push('View name must be a string');
  }

  if (!view.type) {
    errors.push('View type is required');
  } else if (!VALID_VIEW_TYPES.includes(view.type)) {
    errors.push(`Invalid view type: ${view.type}. Must be one of: ${VALID_VIEW_TYPES.join(', ')}`);
  }

  if (!view.dataSource) {
    errors.push('Data source is required');
  } else if (typeof view.dataSource !== 'string') {
    errors.push('Data source must be a string');
  }

  if (!view.config) {
    errors.push('View config is required');
  } else if (typeof view.config !== 'object') {
    errors.push('View config must be an object');
  }

  // Optional fields validation
  if (view.category && typeof view.category !== 'string') {
    warnings.push('Category should be a string');
  }

  if (view.description && typeof view.description !== 'string') {
    warnings.push('Description should be a string');
  }

  if (view.icon && typeof view.icon !== 'string') {
    warnings.push('Icon should be a string');
  }

  if (view.tags && !Array.isArray(view.tags)) {
    warnings.push('Tags should be an array');
  }

  if (view.enabled !== undefined && typeof view.enabled !== 'boolean') {
    warnings.push('Enabled should be a boolean');
  }

  if (view.permissions && !Array.isArray(view.permissions)) {
    warnings.push('Permissions should be an array');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate view type-specific configuration
 */
export function validateViewConfig(view: ViewrView): ViewValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Type-specific validation
  switch (view.type) {
    case 'kanban':
      if (!view.config.columnsSource) {
        errors.push('Kanban view requires columnsSource in config');
      }
      if (!view.config.cardsSource) {
        errors.push('Kanban view requires cardsSource in config');
      }
      if (!view.config.columnFields) {
        errors.push('Kanban view requires columnFields in config');
      }
      if (!view.config.cardFields) {
        errors.push('Kanban view requires cardFields in config');
      }
      break;

    case 'list':
    case 'table':
    case 'spreadsheet':
      if (!view.config.columns || !Array.isArray(view.config.columns)) {
        errors.push(`${view.type} view requires columns array in config`);
      }
      break;

    case 'cards':
      if (!view.config.cardFields) {
        errors.push('Cards view requires cardFields in config');
      }
      break;

    case 'graph':
    case 'network':
      if (!view.config.nodeFields) {
        errors.push(`${view.type} view requires nodeFields in config`);
      }
      if (!view.config.edgeFields) {
        errors.push(`${view.type} view requires edgeFields in config`);
      }
      break;

    case 'timeline':
      if (!view.config.itemFields) {
        errors.push('Timeline view requires itemFields in config');
      }
      break;

    case 'calendar':
      if (!view.config.eventFields) {
        errors.push('Calendar view requires eventFields in config');
      }
      break;

    case 'dashboard':
      if (!view.config.widgets || !Array.isArray(view.config.widgets)) {
        errors.push('Dashboard view requires widgets array in config');
      }
      if (!view.config.layout) {
        errors.push('Dashboard view requires layout in config');
      }
      break;

    case 'form':
      if (!view.config.sections || !Array.isArray(view.config.sections)) {
        errors.push('Form view requires sections array in config');
      }
      break;

    case 'detail':
      if (!view.config.sections || !Array.isArray(view.config.sections)) {
        errors.push('Detail view requires sections array in config');
      }
      break;

    // Other types have flexible config
    default:
      if (Object.keys(view.config).length === 0) {
        warnings.push(`${view.type} view has empty config`);
      }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate complete view (structure + config)
 */
export function validateViewComplete(view: ViewrView): ViewValidationResult {
  const structureResult = validateView(view);
  const configResult = validateViewConfig(view);

  return {
    valid: structureResult.valid && configResult.valid,
    errors: [...structureResult.errors, ...configResult.errors],
    warnings: [...structureResult.warnings, ...configResult.warnings],
  };
}
